import * as i0 from '@angular/core';
import { Directive, InjectionToken, EventEmitter, ElementRef, forwardRef, ContentChild, ViewChild, Output, Input, Inject, Optional, ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { FormControl, ReactiveFormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i5 from '@angular/material/core';
import { MatOption } from '@angular/material/core';
import * as i6 from '@angular/material/form-field';
import { MatFormField } from '@angular/material/form-field';
import * as i4 from '@angular/material/select';
import { MatSelect } from '@angular/material/select';
import { BehaviorSubject, of, combineLatest, Subject } from 'rxjs';
import { switchMap, map, startWith, delay, takeUntil, take, tap, filter } from 'rxjs/operators';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';
import { AsyncPipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import * as i3 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i1 from '@angular/cdk/scrolling';

/**
 * Directive for providing a custom clear-icon.
 * e.g.
 * <ngx-mat-select-search [formControl]="bankFilterCtrl">
 *   <mat-icon ngxMatSelectSearchClear>delete</mat-icon>
 * </ngx-mat-select-search>
 */
const _c0 = ["searchSelectInput"];
const _c1 = ["innerSelectSearch"];
const _c2 = [[["", 8, "mat-select-search-custom-header-content"]], [["", "ngxMatSelectSearchClear", ""]], [["", "ngxMatSelectNoEntriesFound", ""]]];
const _c3 = [".mat-select-search-custom-header-content", "[ngxMatSelectSearchClear]", "[ngxMatSelectNoEntriesFound]"];
function MatSelectSearchComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "mat-checkbox", 10);
    i0.ɵɵlistener("change", function MatSelectSearchComponent_Conditional_4_Template_mat_checkbox_change_0_listener($event) {
      i0.ɵɵrestoreView(_r2);
      const ctx_r2 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r2._emitSelectAllBooleanToParent($event.checked));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("color", ctx_r2.matFormField == null ? null : ctx_r2.matFormField.color)("checked", ctx_r2.toggleAllCheckboxChecked)("indeterminate", ctx_r2.toggleAllCheckboxIndeterminate)("matTooltip", ctx_r2.toggleAllCheckboxTooltipMessage)("matTooltipPosition", ctx_r2.toggleAllCheckboxTooltipPosition);
  }
}
function MatSelectSearchComponent_Conditional_7_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "mat-spinner", 7);
  }
}
function MatSelectSearchComponent_Conditional_8_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0, 1);
  }
}
function MatSelectSearchComponent_Conditional_8_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "mat-icon", 12);
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("svgIcon", ctx_r2.closeSvgIcon);
  }
}
function MatSelectSearchComponent_Conditional_8_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "mat-icon");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r2.closeIcon, " ");
  }
}
function MatSelectSearchComponent_Conditional_8_Template(rf, ctx) {
  if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 11);
    i0.ɵɵlistener("click", function MatSelectSearchComponent_Conditional_8_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r4);
      const ctx_r2 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r2._reset(true));
    });
    i0.ɵɵconditionalCreate(1, MatSelectSearchComponent_Conditional_8_Conditional_1_Template, 1, 0)(2, MatSelectSearchComponent_Conditional_8_Conditional_2_Template, 1, 1, "mat-icon", 12)(3, MatSelectSearchComponent_Conditional_8_Conditional_3_Template, 2, 1, "mat-icon");
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r2.clearIcon ? 1 : ctx_r2.closeSvgIcon ? 2 : 3);
  }
}
function MatSelectSearchComponent_Conditional_11_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0, 2);
  }
}
function MatSelectSearchComponent_Conditional_11_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r2.noEntriesFoundLabel, " ");
  }
}
function MatSelectSearchComponent_Conditional_11_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 9);
    i0.ɵɵconditionalCreate(1, MatSelectSearchComponent_Conditional_11_Conditional_1_Template, 1, 0)(2, MatSelectSearchComponent_Conditional_11_Conditional_2_Template, 1, 1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r2.noEntriesFound ? 1 : 2);
  }
}
let MatSelectSearchClearDirective = /*#__PURE__*/(() => {
  class MatSelectSearchClearDirective {
    static ɵfac = function MatSelectSearchClearDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MatSelectSearchClearDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: MatSelectSearchClearDirective,
      selectors: [["", "ngxMatSelectSearchClear", ""]]
    });
  }
  return MatSelectSearchClearDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/** List of inputs of NgxMatSelectSearchComponent that can be configured with a global default. */
const configurableDefaultOptions = ['ariaLabel', 'clearSearchInput', 'closeIcon', 'closeSvgIcon', 'disableInitialFocus', 'disableScrollToActiveOnOptionsChanged', 'enableClearOnEscapePressed', 'hideClearSearchButton', 'noEntriesFoundLabel', 'placeholderLabel', 'preventHomeEndKeyPropagation', 'searching'];
/**
 * InjectionToken that can be used to specify global options. e.g.
 *
 * ```typescript
 * providers: [
 *   {
 *     provide: MAT_SELECTSEARCH_DEFAULT_OPTIONS,
 *     useValue: <MatSelectSearchOptions>{
 *       closeIcon: 'delete',
 *       noEntriesFoundLabel: 'No options found'
 *     }
 *   }
 * ]
 * ```
 *
 * See the corresponding inputs of `MatSelectSearchComponent` for documentation.
 */
const MAT_SELECTSEARCH_DEFAULT_OPTIONS = /*#__PURE__*/new InjectionToken('mat-selectsearch-default-options');

/**
 * Directive for providing a custom no entries found element.
 * e.g.
 * <ngx-mat-select-search [formControl]="bankFilterCtrl">
 *   <span ngxMatSelectNoEntriesFound>
 *     No entries found <button>Add</button>
 *   </span>
 * </ngx-mat-select-search>
 */
let MatSelectNoEntriesFoundDirective = /*#__PURE__*/(() => {
  class MatSelectNoEntriesFoundDirective {
    static ɵfac = function MatSelectNoEntriesFoundDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MatSelectNoEntriesFoundDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: MatSelectNoEntriesFoundDirective,
      selectors: [["", "ngxMatSelectNoEntriesFound", ""]]
    });
  }
  return MatSelectNoEntriesFoundDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Component providing an input field for searching MatSelect options.
 *
 * Example usage:
 *
 * interface Bank {
 *  id: string;
 *  name: string;
 * }
 *
 * @Component({
 *   selector: 'my-app-data-selection',
 *   template: `
 *     <mat-form-field>
 *       <mat-select [formControl]="bankCtrl" placeholder="Bank">
 *         <mat-option>
 *           <ngx-mat-select-search [formControl]="bankFilterCtrl"></ngx-mat-select-search>
 *         </mat-option>
 *         <mat-option *ngFor="let bank of filteredBanks | async" [value]="bank.id">
 *           {{bank.name}}
 *         </mat-option>
 *       </mat-select>
 *     </mat-form-field>
 *   `
 * })
 * export class DataSelectionComponent implements OnInit, OnDestroy {
 *
 *   // control for the selected bank
 *   public bankCtrl: FormControl = new FormControl();
 *   // control for the MatSelect filter keyword
 *   public bankFilterCtrl: FormControl = new FormControl();
 *
 *   // list of banks
 *   private banks: Bank[] = [{name: 'Bank A', id: 'A'}, {name: 'Bank B', id: 'B'}, {name: 'Bank C', id: 'C'}];
 *   // list of banks filtered by search keyword
 *   public filteredBanks: ReplaySubject<Bank[]> = new ReplaySubject<Bank[]>(1);
 *
 *   // Subject that emits when the component has been destroyed.
 *   private _onDestroy = new Subject<void>();
 *
 *
 *   ngOnInit() {
 *     // load the initial bank list
 *     this.filteredBanks.next(this.banks.slice());
 *     // listen for search field value changes
 *     this.bankFilterCtrl.valueChanges
 *       .pipe(takeUntil(this._onDestroy))
 *       .subscribe(() => {
 *         this.filterBanks();
 *       });
 *   }
 *
 *   ngOnDestroy() {
 *     this._onDestroy.next();
 *     this._onDestroy.complete();
 *   }
 *
 *   private filterBanks() {
 *     if (!this.banks) {
 *       return;
 *     }
 *
 *     // get the search keyword
 *     let search = this.bankFilterCtrl.value;
 *     if (!search) {
 *       this.filteredBanks.next(this.banks.slice());
 *       return;
 *     } else {
 *       search = search.toLowerCase();
 *     }
 *
 *     // filter the banks
 *     this.filteredBanks.next(
 *       this.banks.filter(bank => bank.name.toLowerCase().indexOf(search) > -1)
 *     );
 *   }
 * }
 */
let MatSelectSearchComponent = /*#__PURE__*/(() => {
  class MatSelectSearchComponent {
    matSelect;
    changeDetectorRef;
    _viewportRuler;
    matOption;
    matFormField;
    /** Label of the search placeholder */
    placeholderLabel = 'Suche';
    /** Type of the search input field */
    type = 'text';
    /** Font-based icon used for displaying Close-Icon */
    closeIcon = 'close';
    /** SVG-based icon used for displaying Close-Icon. If set, closeIcon is overridden */
    closeSvgIcon;
    /** Label to be shown when no entries are found. Set to null if no message should be shown. */
    noEntriesFoundLabel = 'Keine Optionen gefunden';
    /**
      * Whether the search field should be cleared after the dropdown menu is closed.
      * Useful for server-side filtering. See [#3](https://github.com/bithost-gmbh/ngx-mat-select-search/issues/3)
      */
    clearSearchInput = true;
    /** Whether to show the search-in-progress indicator */
    searching = false;
    /** Disables initial focusing of the input field */
    disableInitialFocus = false;
    /** Enable clear input on escape pressed */
    enableClearOnEscapePressed = false;
    /**
     * Prevents home / end key being propagated to mat-select,
     * allowing to move the cursor within the search input instead of navigating the options
     */
    preventHomeEndKeyPropagation = false;
    /** Disables scrolling to active options when option list changes. Useful for server-side search */
    disableScrollToActiveOnOptionsChanged = false;
    /** Adds 508 screen reader support for search box */
    ariaLabel = 'dropdown search';
    /** Whether to show Select All Checkbox (for mat-select[multi=true]) */
    showToggleAllCheckbox = false;
    /** Select all checkbox checked state */
    toggleAllCheckboxChecked = false;
    /** select all checkbox indeterminate state */
    toggleAllCheckboxIndeterminate = false;
    /** Display a message in a tooltip on the toggle-all checkbox */
    toggleAllCheckboxTooltipMessage = '';
    /** Define the position of the tooltip on the toggle-all checkbox. */
    toggleAllCheckboxTooltipPosition = 'below';
    /** Show/Hide the search clear button of the search input */
    hideClearSearchButton = false;
    /**
     * Always restore selected options on selectionChange for mode multi (e.g. for lazy loading/infinity scrolling).
     * Defaults to false, so selected options are only restored while filtering is active.
     */
    alwaysRestoreSelectedOptionsMulti = false;
    /**
     * Recreate array of selected values for multi-selects.
     *
     * This is useful if the selected values are stored in an immutable data structure.
     */
    recreateValuesArray = false;
    /** Output emitter to send to parent component with the toggle all boolean */
    toggleAll = new EventEmitter();
    /** Reference to the search input field */
    searchSelectInput;
    /** Reference to the search input field */
    innerSelectSearch;
    /** Reference to custom search input clear icon */
    clearIcon;
    /** Reference to custom no entries found element */
    noEntriesFound;
    /** Current search value */
    get value() {
      return this._formControl.value;
    }
    _lastExternalInputValue;
    onTouched = () => {
      // do nothing.
    };
    /** Reference to the MatSelect options */
    set _options(_options) {
      this._options$.next(_options);
    }
    get _options() {
      return this._options$.getValue();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _options$ = new BehaviorSubject(null);
    optionsList$ = this._options$.pipe(switchMap(_options => _options ? _options.changes.pipe(map(options => options.toArray()), startWith(_options.toArray())) : of(null)));
    optionsLength$ = this.optionsList$.pipe(map(options => options ? options.length : 0));
    /** Previously selected values when using <mat-select [multiple]="true">*/
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    previousSelectedValues;
    _formControl = new FormControl('', {
      nonNullable: true
    });
    /** Whether to show the no entries found message */
    _showNoEntriesFound$ = combineLatest([this._formControl.valueChanges, this.optionsLength$]).pipe(map(([value, optionsLength]) => !!(this.noEntriesFoundLabel && value && optionsLength === this.getOptionsLengthOffset())));
    /** Subject that emits when the component has been destroyed. */
    _onDestroy = new Subject();
    /** Reference to active descendant for ARIA Support. */
    activeDescendant;
    constructor(matSelect, changeDetectorRef, _viewportRuler, matOption, matFormField, defaultOptions) {
      this.matSelect = matSelect;
      this.changeDetectorRef = changeDetectorRef;
      this._viewportRuler = _viewportRuler;
      this.matOption = matOption;
      this.matFormField = matFormField;
      this.applyDefaultOptions(defaultOptions);
    }
    applyDefaultOptions(defaultOptions) {
      if (!defaultOptions) {
        return;
      }
      for (const key of configurableDefaultOptions) {
        if (Object.prototype.hasOwnProperty.call(defaultOptions, key)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this[key] = defaultOptions[key];
        }
      }
    }
    ngOnInit() {
      // set custom mat-option class if the component was placed inside a mat-option
      if (this.matOption) {
        this.matOption.disabled = true;
        this.matOption._getHostElement().classList.add('contains-mat-select-search');
        this.matOption._getHostElement().setAttribute('role', 'presentation');
      } else {
        console.error('<ngx-mat-select-search> must be placed inside a <mat-option> element');
      }
      // when the select dropdown panel is opened or closed
      this.matSelect.openedChange.pipe(delay(1), takeUntil(this._onDestroy)).subscribe(opened => {
        if (opened) {
          this.updateInputWidth();
          // focus the search field when opening
          if (!this.disableInitialFocus) {
            this._focus();
          }
        } else {
          // clear it when closing
          if (this.clearSearchInput) {
            this._reset();
          }
        }
      });
      // set the first item active after the options changed
      this.matSelect.openedChange.pipe(take(1), switchMap(() => {
        this._options = this.matSelect.options;
        // Closure variable for tracking the most recent first option.
        // In order to avoid causing the list to
        // scroll to the top when options are added to the bottom of
        // the list (eg: infinite scroll), we compare only
        // the changes to the first options to determine if we
        // should set the first item as active.
        // This prevents unnecessary scrolling to the top of the list
        // when options are appended, but allows the first item
        // in the list to be set as active by default when there
        // is no active selection
        let previousFirstOption = this._options.toArray()[this.getOptionsLengthOffset()];
        return this._options.changes.pipe(tap(() => {
          // avoid "expression has been changed" error
          setTimeout(() => {
            // Convert the QueryList to an array
            const options = this._options.toArray();
            // The true first item is offset by 1
            const currentFirstOption = options[this.getOptionsLengthOffset()];
            const keyManager = this.matSelect._keyManager;
            if (keyManager && this.matSelect.panelOpen && currentFirstOption) {
              // set first item active and input width
              // Check to see if the first option in these changes is different from the previous.
              const firstOptionIsChanged = !previousFirstOption || !this.matSelect.compareWith(previousFirstOption.value, currentFirstOption.value);
              // CASE: The first option is different now.
              // Indicates we should set it as active and scroll to the top.
              if (firstOptionIsChanged || !keyManager.activeItem || !options.find(option => this.matSelect.compareWith(option.value, keyManager.activeItem?.value))) {
                keyManager.setActiveItem(this.getOptionsLengthOffset());
              }
              // wait for panel width changes
              setTimeout(() => {
                this.updateInputWidth();
              });
            }
            // Update our reference
            previousFirstOption = currentFirstOption;
          });
        }));
      })).pipe(takeUntil(this._onDestroy)).subscribe();
      // add or remove css class depending on whether to show the no entries found message
      // note: this is hacky
      this._showNoEntriesFound$.pipe(takeUntil(this._onDestroy)).subscribe(showNoEntriesFound => {
        // set no entries found class on mat option
        if (this.matOption) {
          if (showNoEntriesFound) {
            this.matOption._getHostElement().classList.add('mat-select-search-no-entries-found');
          } else {
            this.matOption._getHostElement().classList.remove('mat-select-search-no-entries-found');
          }
        }
      });
      // resize the input width when the viewport is resized, i.e. the trigger width could potentially be resized
      this._viewportRuler.change().pipe(takeUntil(this._onDestroy)).subscribe(() => {
        if (this.matSelect.panelOpen) {
          this.updateInputWidth();
        }
      });
      this.initMultipleHandling();
      this.optionsList$.pipe(takeUntil(this._onDestroy)).subscribe(() => {
        // update view when available options change
        this.changeDetectorRef.markForCheck();
      });
    }
    _emitSelectAllBooleanToParent(state) {
      this.toggleAll.emit(state);
    }
    ngOnDestroy() {
      this._onDestroy.next();
      this._onDestroy.complete();
    }
    _isToggleAllCheckboxVisible() {
      return this.matSelect.multiple && this.showToggleAllCheckbox;
    }
    /**
     * Handles the key down event with MatSelect.
     * Allows e.g. selecting with enter key, navigation with arrow keys, etc.
     * @param event
     */
    _handleKeydown(event) {
      // Prevent propagation for all alphanumeric characters in order to avoid selection issues
      // Needed to avoid handling in https://github.com/angular/components/blob/5439460d1fe166f8ec34ab7d48f05e0dd7f6a946/src/material/select/select.ts#L965
      if (event.key && event.key.length === 1 || this.preventHomeEndKeyPropagation && (event.key === 'Home' || event.key === 'End')) {
        event.stopPropagation();
      }
      if (this.matSelect.multiple && event.key && event.key === 'Enter') {
        // Regain focus after multiselect, so we can further type
        setTimeout(() => this._focus());
      }
      // Special case if click Escape, if input is empty, close the dropdown, if not, empty out the search field
      if (this.enableClearOnEscapePressed && event.key === 'Escape' && this.value) {
        this._reset(true);
        event.stopPropagation();
      }
    }
    /**
     * Handles the key up event with MatSelect.
     * Allows e.g. the announcing of the currently activeDescendant by screen readers.
     */
    _handleKeyup(event) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const ariaActiveDescendantId = this.matSelect._getAriaActiveDescendant();
        const index = this._options.toArray().findIndex(item => item.id === ariaActiveDescendantId);
        if (index !== -1) {
          this.unselectActiveDescendant();
          this.activeDescendant = this._options.toArray()[index]._getHostElement();
          this.activeDescendant.setAttribute('aria-selected', 'true');
          this.searchSelectInput.nativeElement.setAttribute('aria-activedescendant', ariaActiveDescendantId);
        }
      }
    }
    writeValue(value) {
      this._lastExternalInputValue = value;
      this._formControl.setValue(value);
      this.changeDetectorRef.markForCheck();
    }
    onBlur() {
      this.unselectActiveDescendant();
      this.onTouched();
    }
    registerOnChange(fn) {
      this._formControl.valueChanges.pipe(filter(value => value !== this._lastExternalInputValue), tap(() => this._lastExternalInputValue = undefined), takeUntil(this._onDestroy)).subscribe(fn);
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    /**
     * Focuses the search input field
     */
    _focus() {
      if (!this.searchSelectInput || !this.matSelect.panel) {
        return;
      }
      // save and restore scrollTop of panel, since it will be reset by focus()
      // note: this is hacky
      const panel = this.matSelect.panel.nativeElement;
      const scrollTop = panel.scrollTop;
      // focus
      this.searchSelectInput.nativeElement.focus();
      panel.scrollTop = scrollTop;
    }
    /**
     * Resets the current search value
     * @param focus whether to focus after resetting
     */
    _reset(focus) {
      this._formControl.setValue('');
      if (focus) {
        this._focus();
      }
    }
    /**
     * Initializes handling <mat-select [multiple]="true">
     * Note: to improve this code, mat-select should be extended to allow disabling resetting the selection while filtering.
     */
    initMultipleHandling() {
      if (!this.matSelect.ngControl) {
        if (this.matSelect.multiple) {
          // note: the access to matSelect.ngControl (instead of matSelect.value / matSelect.valueChanges)
          // is necessary to properly work in multi-selection mode.
          console.error('the mat-select containing ngx-mat-select-search must have a ngModel or formControl directive when multiple=true');
        }
        return;
      }
      // if <mat-select [multiple]="true">
      // store previously selected values and restore them when they are deselected
      // because the option is not available while we are currently filtering
      this.previousSelectedValues = this.matSelect.ngControl.value;
      if (!this.matSelect.ngControl.valueChanges) {
        return;
      }
      this.matSelect.ngControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(values => {
        let restoreSelectedValues = false;
        if (this.matSelect.multiple) {
          if ((this.alwaysRestoreSelectedOptionsMulti || this._formControl.value && this._formControl.value.length) && this.previousSelectedValues && Array.isArray(this.previousSelectedValues)) {
            if (!values || !Array.isArray(values)) {
              values = [];
            }
            const optionValues = this.matSelect.options.map(option => option.value);
            this.previousSelectedValues.forEach(previousValue => {
              if (!values.some(v => this.matSelect.compareWith(v, previousValue)) && !optionValues.some(v => this.matSelect.compareWith(v, previousValue))) {
                // if a value that was selected before is deselected and not found in the options, it was deselected
                // due to the filtering, so we restore it.
                if (this.recreateValuesArray) {
                  values = [...values, previousValue];
                } else {
                  values.push(previousValue);
                }
                restoreSelectedValues = true;
              }
            });
          }
        }
        this.previousSelectedValues = values;
        if (restoreSelectedValues) {
          this.matSelect._onChange(values);
        }
      });
    }
    /**
     *  Set the width of the innerSelectSearch to fit even custom scrollbars
     *  And support all Operating Systems
     */
    updateInputWidth() {
      if (!this.innerSelectSearch || !this.innerSelectSearch.nativeElement) {
        return;
      }
      let element = this.innerSelectSearch.nativeElement;
      let panelElement = null;
      while (element && element.parentElement) {
        element = element.parentElement;
        if (element.classList.contains('mat-select-panel')) {
          panelElement = element;
          break;
        }
      }
      if (panelElement) {
        this.innerSelectSearch.nativeElement.style.width = panelElement.clientWidth + 'px';
      }
    }
    /**
     * Determine the offset to length that can be caused by the optional matOption used as a search input.
     */
    getOptionsLengthOffset() {
      if (this.matOption) {
        return 1;
      } else {
        return 0;
      }
    }
    unselectActiveDescendant() {
      this.activeDescendant?.removeAttribute('aria-selected');
      this.searchSelectInput.nativeElement.removeAttribute('aria-activedescendant');
    }
    static ɵfac = function MatSelectSearchComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MatSelectSearchComponent)(i0.ɵɵdirectiveInject(MatSelect), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.ViewportRuler), i0.ɵɵdirectiveInject(MatOption, 8), i0.ɵɵdirectiveInject(MatFormField, 8), i0.ɵɵdirectiveInject(MAT_SELECTSEARCH_DEFAULT_OPTIONS, 8));
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: MatSelectSearchComponent,
      selectors: [["ngx-mat-select-search"]],
      contentQueries: function MatSelectSearchComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, MatSelectSearchClearDirective, 5);
          i0.ɵɵcontentQuery(dirIndex, MatSelectNoEntriesFoundDirective, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.clearIcon = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.noEntriesFound = _t.first);
        }
      },
      viewQuery: function MatSelectSearchComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 7, ElementRef);
          i0.ɵɵviewQuery(_c1, 7, ElementRef);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.searchSelectInput = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.innerSelectSearch = _t.first);
        }
      },
      inputs: {
        placeholderLabel: "placeholderLabel",
        type: "type",
        closeIcon: "closeIcon",
        closeSvgIcon: "closeSvgIcon",
        noEntriesFoundLabel: "noEntriesFoundLabel",
        clearSearchInput: "clearSearchInput",
        searching: "searching",
        disableInitialFocus: "disableInitialFocus",
        enableClearOnEscapePressed: "enableClearOnEscapePressed",
        preventHomeEndKeyPropagation: "preventHomeEndKeyPropagation",
        disableScrollToActiveOnOptionsChanged: "disableScrollToActiveOnOptionsChanged",
        ariaLabel: "ariaLabel",
        showToggleAllCheckbox: "showToggleAllCheckbox",
        toggleAllCheckboxChecked: "toggleAllCheckboxChecked",
        toggleAllCheckboxIndeterminate: "toggleAllCheckboxIndeterminate",
        toggleAllCheckboxTooltipMessage: "toggleAllCheckboxTooltipMessage",
        toggleAllCheckboxTooltipPosition: "toggleAllCheckboxTooltipPosition",
        hideClearSearchButton: "hideClearSearchButton",
        alwaysRestoreSelectedOptionsMulti: "alwaysRestoreSelectedOptionsMulti",
        recreateValuesArray: "recreateValuesArray"
      },
      outputs: {
        toggleAll: "toggleAll"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => MatSelectSearchComponent),
        multi: true
      }])],
      ngContentSelectors: _c3,
      decls: 13,
      vars: 14,
      consts: [["innerSelectSearch", ""], ["searchSelectInput", ""], ["matInput", "", 1, "mat-select-search-input", "mat-select-search-hidden"], [1, "mat-select-search-inner", "mat-typography", "mat-datepicker-content", "mat-tab-header"], [1, "mat-select-search-inner-row"], ["matTooltipClass", "ngx-mat-select-search-toggle-all-tooltip", 1, "mat-select-search-toggle-all-checkbox", 3, "color", "checked", "indeterminate", "matTooltip", "matTooltipPosition"], ["autocomplete", "off", 1, "mat-select-search-input", 3, "keydown", "keyup", "blur", "type", "formControl", "placeholder"], ["diameter", "16", 1, "mat-select-search-spinner"], ["mat-icon-button", "", "aria-label", "Clear", 1, "mat-select-search-clear"], [1, "mat-select-search-no-entries-found"], ["matTooltipClass", "ngx-mat-select-search-toggle-all-tooltip", 1, "mat-select-search-toggle-all-checkbox", 3, "change", "color", "checked", "indeterminate", "matTooltip", "matTooltipPosition"], ["mat-icon-button", "", "aria-label", "Clear", 1, "mat-select-search-clear", 3, "click"], [3, "svgIcon"]],
      template: function MatSelectSearchComponent_Template(rf, ctx) {
        if (rf & 1) {
          const _r1 = i0.ɵɵgetCurrentView();
          i0.ɵɵprojectionDef(_c2);
          i0.ɵɵelement(0, "input", 2);
          i0.ɵɵelementStart(1, "div", 3, 0)(3, "div", 4);
          i0.ɵɵconditionalCreate(4, MatSelectSearchComponent_Conditional_4_Template, 1, 5, "mat-checkbox", 5);
          i0.ɵɵelementStart(5, "input", 6, 1);
          i0.ɵɵlistener("keydown", function MatSelectSearchComponent_Template_input_keydown_5_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx._handleKeydown($event));
          })("keyup", function MatSelectSearchComponent_Template_input_keyup_5_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx._handleKeyup($event));
          })("blur", function MatSelectSearchComponent_Template_input_blur_5_listener() {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.onBlur());
          });
          i0.ɵɵelementEnd();
          i0.ɵɵconditionalCreate(7, MatSelectSearchComponent_Conditional_7_Template, 1, 0, "mat-spinner", 7);
          i0.ɵɵconditionalCreate(8, MatSelectSearchComponent_Conditional_8_Template, 4, 1, "button", 8);
          i0.ɵɵprojection(9);
          i0.ɵɵelementEnd();
          i0.ɵɵelement(10, "mat-divider");
          i0.ɵɵelementEnd();
          i0.ɵɵconditionalCreate(11, MatSelectSearchComponent_Conditional_11_Template, 3, 1, "div", 9);
          i0.ɵɵpipe(12, "async");
        }
        if (rf & 2) {
          i0.ɵɵadvance();
          i0.ɵɵclassProp("mat-select-search-inner-multiple", ctx.matSelect.multiple)("mat-select-search-inner-toggle-all", ctx._isToggleAllCheckboxVisible());
          i0.ɵɵadvance(3);
          i0.ɵɵconditional(ctx._isToggleAllCheckboxVisible() ? 4 : -1);
          i0.ɵɵadvance();
          i0.ɵɵproperty("type", ctx.type)("formControl", ctx._formControl)("placeholder", ctx.placeholderLabel);
          i0.ɵɵattribute("aria-label", ctx.ariaLabel);
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.searching ? 7 : -1);
          i0.ɵɵadvance();
          i0.ɵɵconditional(!ctx.hideClearSearchButton && ctx.value && !ctx.searching ? 8 : -1);
          i0.ɵɵadvance(3);
          i0.ɵɵconditional(i0.ɵɵpipeBind1(12, 12, ctx._showNoEntriesFound$) ? 11 : -1);
        }
      },
      dependencies: [AsyncPipe, ReactiveFormsModule, i2.DefaultValueAccessor, i2.NgControlStatus, i2.FormControlDirective, MatCheckbox, MatDivider, MatTooltip, MatProgressSpinner, MatIcon, MatButtonModule, i3.MatIconButton],
      styles: [".mat-select-search-hidden[_ngcontent-%COMP%]{visibility:hidden}.mat-select-search-inner[_ngcontent-%COMP%]{position:absolute;top:0;left:0;width:100%;z-index:100;font-size:inherit;box-shadow:none;background-color:var(--mat-sys-surface-container, var(--mat-select-panel-background-color, white))}.mat-select-search-inner.mat-select-search-inner-multiple.mat-select-search-inner-toggle-all[_ngcontent-%COMP%]   .mat-select-search-inner-row[_ngcontent-%COMP%]{display:flex;align-items:center}.mat-select-search-input[_ngcontent-%COMP%]{box-sizing:border-box;width:100%;border:none;font-family:inherit;font-size:inherit;color:currentColor;outline:none;background-color:var(--mat-sys-surface-container, var(--mat-select-panel-background-color, white));padding:0 44px 0 16px;height:47px;line-height:47px}[dir=rtl][_nghost-%COMP%]   .mat-select-search-input[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .mat-select-search-input[_ngcontent-%COMP%]{padding-right:16px;padding-left:44px}.mat-select-search-input[_ngcontent-%COMP%]::placeholder{color:var(--mdc-filled-text-field-input-text-placeholder-color)}.mat-select-search-inner-toggle-all[_ngcontent-%COMP%]   .mat-select-search-input[_ngcontent-%COMP%]{padding-left:5px}.mat-select-search-no-entries-found[_ngcontent-%COMP%]{padding-top:8px}.mat-select-search-clear[_ngcontent-%COMP%]{position:absolute;right:4px;top:0}[dir=rtl][_nghost-%COMP%]   .mat-select-search-clear[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .mat-select-search-clear[_ngcontent-%COMP%]{right:auto;left:4px}.mat-select-search-spinner[_ngcontent-%COMP%]{position:absolute;right:16px;top:calc(50% - 8px)}[dir=rtl][_nghost-%COMP%]   .mat-select-search-spinner[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .mat-select-search-spinner[_ngcontent-%COMP%]{right:auto;left:16px}  .mat-mdc-option[aria-disabled=true].contains-mat-select-search{position:sticky;top:-8px;z-index:1;opacity:1;margin-top:-8px;pointer-events:all}  .mat-mdc-option[aria-disabled=true].contains-mat-select-search .mat-icon{margin-right:0;margin-left:0}  .mat-mdc-option[aria-disabled=true].contains-mat-select-search mat-pseudo-checkbox{display:none}  .mat-mdc-option[aria-disabled=true].contains-mat-select-search .mdc-list-item__primary-text{opacity:1}.mat-select-search-toggle-all-checkbox[_ngcontent-%COMP%]{padding-left:5px}[dir=rtl][_nghost-%COMP%]   .mat-select-search-toggle-all-checkbox[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .mat-select-search-toggle-all-checkbox[_ngcontent-%COMP%]{padding-left:0;padding-right:5px}"],
      changeDetection: 0
    });
  }
  return MatSelectSearchComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Copyright (c) 2018 Bithost GmbH All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const MatSelectSearchVersion = '8.0.3';
let NgxMatSelectSearchModule = /*#__PURE__*/(() => {
  class NgxMatSelectSearchModule {
    static ɵfac = function NgxMatSelectSearchModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxMatSelectSearchModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NgxMatSelectSearchModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [MatSelectSearchComponent]
    });
  }
  return NgxMatSelectSearchModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { MAT_SELECTSEARCH_DEFAULT_OPTIONS, MatSelectNoEntriesFoundDirective, MatSelectSearchClearDirective, MatSelectSearchComponent, MatSelectSearchVersion, NgxMatSelectSearchModule, configurableDefaultOptions };
